import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Shield, EnvelopeSimple, Lock, Eye, EyeSlash, User, Warning } from '@phosphor-icons/react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_compliance-hub-623/artifacts/04kw0jh0_IMG_4683.jpeg';

export const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [referralSource, setReferralSource] = useState('');
  const [referralSourceOther, setReferralSourceOther] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    const extras = {};
    if (referralSource) {
      extras.referral_source = referralSource;
      if (referralSource === 'other' && referralSourceOther.trim()) {
        extras.referral_source_other = referralSourceOther.trim();
      }
    }
    const result = await register(email, password, name, extras);
    
    if (result.success) {
      navigate('/subscribe');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-2">
            <img src={LOGO_URL} alt="Vantage" className="h-10 w-auto" />
            <span className="text-xl font-bold tracking-tight">Vantage Fleet</span>
          </Link>
        </div>
        
        {/* Form Card */}
        <div className="border border-border bg-background p-8">
          <h1 className="text-2xl font-semibold tracking-tight mb-2">Create an account</h1>
          <p className="text-muted-foreground mb-6">Get started with your subscription</p>
          
          {error && (
            <div className="flex items-center gap-2 p-3 mb-6 bg-destructive/10 border border-destructive/20 text-destructive text-sm" data-testid="register-error">
              <Warning weight="bold" className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <div className="relative">
                <User weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="name"
                  type="text"
                  placeholder="John Smith"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10 rounded-sm"
                  data-testid="register-name-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <EnvelopeSimple weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 rounded-sm"
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 rounded-sm"
                  required
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeSlash weight="duotone" className="h-5 w-5" /> : <Eye weight="duotone" className="h-5 w-5" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">Must be at least 6 characters</p>
            </div>

            {/* Optional: Where did you hear about us? */}
            <div className="space-y-2">
              <Label htmlFor="referral">
                How did you hear about us? <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <Select value={referralSource} onValueChange={setReferralSource}>
                <SelectTrigger
                  id="referral"
                  className="rounded-sm h-11"
                  data-testid="register-referral-select"
                >
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="facebook">Facebook</SelectItem>
                  <SelectItem value="linkedin">LinkedIn</SelectItem>
                  <SelectItem value="search">Search engine (Google etc.)</SelectItem>
                  <SelectItem value="word_of_mouth">Word of mouth / colleague</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              {referralSource === 'other' && (
                <Input
                  type="text"
                  placeholder="Please tell us where..."
                  value={referralSourceOther}
                  onChange={(e) => setReferralSourceOther(e.target.value)}
                  maxLength={120}
                  className="rounded-sm h-11"
                  data-testid="register-referral-other-input"
                />
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full rounded-sm h-11" 
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>
          
          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">Already have an account? </span>
            <Link to="/login" className="text-primary hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </div>
        </div>
        
        <p className="text-xs text-muted-foreground text-center mt-6">
          By creating an account, you agree that this tool is for guidance only and does not constitute legal advice.
        </p>
      </div>
    </div>
  );
};
